import type { Rng, RoundOutcome } from '../../core/contracts'

/**
 * Reaction Target - зрительный поиск и скорость.
 *
 * Поле одинаковых стрелок, одна повёрнута иначе. Нужно найти её
 * и нажать. Классическая проба на избирательное внимание: чем меньше
 * разница в повороте и чем больше поле, тем дольше поиск.
 *
 * Различие по ФОРМЕ, а не по цвету - в отличие от Stroop, игра
 * доступна людям с нарушением цветовосприятия.
 *
 * Как и в Stroop, раунд - это блок проб: по одной пробе точность
 * была бы 0 или 1, а время реакции слишком шумным.
 */

export interface TargetTrial {
  /** Сторона поля: 4 = 4x4 */
  readonly gridSize: number
  /** Индекс отличающейся стрелки - правильный ответ */
  readonly targetIndex: number
  /** Поворот всех обычных стрелок, градусы */
  readonly baseAngle: number
  /** На сколько градусов отличается цель */
  readonly deltaAngle: number
}

export interface TargetRound {
  readonly trials: readonly TargetTrial[]
  /** Лимит на пробу; не успел - засчитывается как ошибка */
  readonly timeLimitMs: number
}

export interface TargetAnswer {
  /** Индекс нажатой ячейки на каждой пробе; null - не успел */
  readonly picks: readonly (number | null)[]
  /** Среднее время верных ответов, мс */
  readonly rtMs: number
}

export interface LevelSpec {
  readonly gridSize: number
  readonly trials: number
  /**
   * Разница поворота цели, градусы. Главный источник трудности:
   * 180 градусов видно мгновенно, 30 приходится искать.
   */
  readonly deltaAngle: number
  readonly timeLimitMs: number
}

const LEVELS: readonly LevelSpec[] = [
  { gridSize: 3, trials: 6, deltaAngle: 180, timeLimitMs: 4000 },
  { gridSize: 3, trials: 6, deltaAngle: 180, timeLimitMs: 3700 },
  { gridSize: 4, trials: 6, deltaAngle: 180, timeLimitMs: 3500 },
  { gridSize: 4, trials: 6, deltaAngle: 120, timeLimitMs: 3400 },
  { gridSize: 4, trials: 8, deltaAngle: 90, timeLimitMs: 3300 },
  { gridSize: 5, trials: 8, deltaAngle: 90, timeLimitMs: 3200 },
  { gridSize: 5, trials: 8, deltaAngle: 60, timeLimitMs: 3100 },
  { gridSize: 5, trials: 8, deltaAngle: 45, timeLimitMs: 3000 },
  { gridSize: 6, trials: 8, deltaAngle: 45, timeLimitMs: 2900 },
  { gridSize: 6, trials: 10, deltaAngle: 35, timeLimitMs: 2800 },
  { gridSize: 6, trials: 10, deltaAngle: 30, timeLimitMs: 2700 },
  { gridSize: 7, trials: 10, deltaAngle: 25, timeLimitMs: 2600 },
]

export const MIN_LEVEL = 1
export const MAX_LEVEL = LEVELS.length
export const INITIAL_LEVEL = 3

/** Рамки лимита после применения личного темпа. */
export const MIN_TIME_LIMIT_MS = 1600
export const MAX_TIME_LIMIT_MS = 7000

/** Эталонное время: типичный зрительный поиск в такой задаче. */
export const REFERENCE_RT_MS = 1600

export function specForLevel(level: number): LevelSpec {
  const index = Math.min(Math.max(level, MIN_LEVEL), MAX_LEVEL) - 1
  const spec = LEVELS[index]
  if (!spec) throw new Error(`Reaction Target: нет спецификации для уровня ${level}`)
  return spec
}

/**
 * @param pace личный темп игрока: лимит растягивается под тех,
 *   кто обычно ищет дольше эталона.
 */
export function generateRound(level: number, rng: Rng, pace = 1): TargetRound {
  const spec = specForLevel(level)
  const cells = spec.gridSize * spec.gridSize

  const trials: TargetTrial[] = []
  for (let i = 0; i < spec.trials; i++) {
    trials.push({
      gridSize: spec.gridSize,
      targetIndex: rng.int(cells),
      // базовый поворот случайный, иначе игрок запомнит «цель всегда вниз»
      baseAngle: rng.int(12) * 30,
      deltaAngle: spec.deltaAngle,
    })
  }

  const timeLimitMs = Math.round(
    Math.min(Math.max(spec.timeLimitMs * pace, MIN_TIME_LIMIT_MS), MAX_TIME_LIMIT_MS),
  )

  return { trials, timeLimitMs }
}

export function evaluate(round: TargetRound, answer: TargetAnswer): RoundOutcome {
  const total = round.trials.length
  if (total === 0) return { correct: false, accuracy: 0, rtMs: answer.rtMs }

  let hits = 0
  round.trials.forEach((trial, i) => {
    if (answer.picks[i] === trial.targetIndex) hits++
  })

  return {
    correct: hits === total,
    accuracy: hits / total,
    rtMs: answer.rtMs,
  }
}

/** Угол поворота конкретной ячейки. Для отрисовки и тестов. */
export function angleOf(trial: TargetTrial, cell: number): number {
  return cell === trial.targetIndex ? trial.baseAngle + trial.deltaAngle : trial.baseAngle
}
